import React, { useEffect, useMemo, useState } from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "react-toastify";
import axios from "axios";
import { Modal, OrderGroupContainer } from "../../../styled/setting";
import Info from "../../common/info";
import { IoClose } from "react-icons/io5";

const OrderGroup = ({
  data,
  selectCompany,
  getMenuGroup,
  openModal,
  closeModal,
  clickImages,
  toggleModal,
}) => {
  const [groupOrder, setGroupOrder] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(null);

  useEffect(() => {
    setGroupOrder([...data]);
  }, [data]);

  const handleDragStart = ({ active, activatorEvent }) => {
    setActiveId(active.id);
    setIsDragging(true);

    if (activatorEvent && "clientX" in activatorEvent && "clientY" in activatorEvent) {
      setCursorPosition({
        x: activatorEvent.clientX,
        y: activatorEvent.clientY,
      });
    }
  };

  useEffect(() => {
    const handleMove = (e) => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    };

    if (isDragging) {
      window.addEventListener("pointermove", handleMove);
    }

    return () => {
      window.removeEventListener("pointermove", handleMove);
    };
  }, [isDragging]);

  const handleDragEnd = ({ active, over }) => {
    if (active?.id !== over?.id) {
      const oldIndex = groupOrder.findIndex((item) => item.id === active.id);
      const newIndex = groupOrder.findIndex((item) => item.id === over.id);
      const newOrder = arrayMove(groupOrder, oldIndex, newIndex);
      setGroupOrder(newOrder);
    }

    setActiveId(null);
    setIsDragging(false);
    setCursorPosition(null);
  };

  const saveGroupOrder = async () => {
    const url = "/erp/menuGroup/order";
    const body = {
      cmpnyNo: selectCompany.value,
      input: groupOrder.map((g, idx) => ({ id: g.id, sortOrder: idx })),
    };

    try {
      const res = await axios.post(url, body);
      if (res.status === 200) {
        toast.success("순서가 변경되었습니다.");
        await getMenuGroup();
      }
    } catch (error) {
      console.error(error);
    } finally {
      closeModal();
    }
  };

  const SortableItem = React.memo(({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: id === activeId ? 0.5 : 1,
    };

    return (
      <li ref={setNodeRef} style={style} {...attributes} {...listeners}>
        {children}
      </li>
    );
  });

  return (
    <Modal onClick={(e) => e.target === e.currentTarget && toggleModal()}>
      <div className="order modal">
        <OrderGroupContainer>
          <button className="closeModal" onClick={() => toggleModal()}>
            <IoClose />
          </button>
          <Info
            message={`메뉴 그룹 순서 변경`}
            actions={[
              {
                label: "되돌리기",
                func: () => setGroupOrder([...data]),
                key: "cancel",
              },
              {
                label: "확인",
                func: () => {
                  const isChanged = data.some((item, i) => item.id !== groupOrder[i]?.id);

                  if (!isChanged) {
                    toast.error("변경된 내용이 없습니다.");
                    return;
                  }

                  openModal({
                    type: "confirmation",
                    title: "순서 변경",
                    message: ["메뉴 그룹을 해당 순서로 변경하시겠습니까?"],
                    handleConfirm: () => saveGroupOrder(),
                  });
                },
                key: "add",
              },
            ]}
          />
          <div className="contents">
            <DndContext
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}>
              <SortableContext
                items={groupOrder.map((g) => g.id)}
                strategy={verticalListSortingStrategy}>
                <ul className="menuGroups">
                  {groupOrder.map((item) => (
                    <SortableItem key={item.id} id={item.id}>
                      <div>{item.name}</div>
                    </SortableItem>
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          </div>
        </OrderGroupContainer>
      </div>
    </Modal>
  );
};

export default OrderGroup;
